import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getConsent, setConsent, ga4Id } from "@/lib/ga4";

/**
 * Quiet, on-brand consent strip. Only shows if:
 *   1) a GA4 id is actually configured, AND
 *   2) the visitor has not yet chosen accept or decline.
 * Persists choice in localStorage.
 */
export default function ConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!ga4Id()) return;
    if (!getConsent()) setVisible(true);
  }, []);

  const accept = () => { setConsent("accepted"); setVisible(false); };
  const decline = () => { setConsent("declined"); setVisible(false); };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          role="dialog"
          aria-label="Analytics consent"
          data-testid="consent-banner"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-x-4 bottom-4 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:max-w-md z-50"
        >
          <div className="rounded-xl border border-brand-rule/70 bg-brand-cream/95 backdrop-blur-md shadow-[0_20px_60px_-30px_rgba(26,37,64,0.35)] px-5 py-4 sm:px-6 sm:py-5">
            <p className="text-[14px] leading-relaxed text-brand-ink/80">
              We'd like to learn — quietly — what resonates on this site. May we use
              privacy-respecting analytics to help improve the words and pages you're reading?
            </p>
            <div className="mt-4 flex items-center gap-4">
              <button
                type="button"
                onClick={accept}
                data-testid="consent-accept"
                className="rounded-full bg-brand-ink px-5 py-2 text-brand-cream text-[13px] tracking-wide hover:bg-brand-blue transition-colors"
              >
                Yes, that's fine
              </button>
              <button
                type="button"
                onClick={decline}
                data-testid="consent-decline"
                className="text-[13px] text-brand-ink/60 hover:text-brand-ink transition-colors underline-offset-4 hover:underline"
              >
                No thank you
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
